"use client"

import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import { useState, useEffect, type FormEvent } from "react"
import type React from "react"

import MODAL_NAMES from "../ModalName"
import { hideModalFromId } from "../../../shared/functions/ModalShow"

import { defaultUser, type IUser } from "../../../shared/models/User"
import "./ViewTrainingRequests.scss" // Import the newly created SCSS file
import { dateFormat_YY_MM_DD } from "../../shared/utils/utils"

import { MAIL_EMAIL, USER_APPROVAL_EMAIL_TEMPLATE } from "../../../shared/functions/mailMessages"
import { type ITrainingRequest, defaultTrainingRequest } from "../../../shared/models/TrainingRequestModel"
import type { ITrainingBudget } from "../../../shared/models/TrainingBudgetModal"

const ApproveTrainingRequest = observer(() => {
  const { store, api } = useAppContext()
  const me = store.auth.me?.asJson
  const departments = store.department.all
  const [currentUser, setCurrentUser] = useState<IUser>({ ...defaultUser })
  const [courseRequest, setCourseRequest] = useState<ITrainingRequest>({
    ...defaultTrainingRequest,
  })
  const [loading, setLoading] = useState(false)
  const allBudget = store.trainingBudget.all //budget stats
  const internalBudget = allBudget.map((training) => training.asJson).find((training) => training.type === "Internal")
  const externalBudget = allBudget.map((training) => training.asJson).find((training) => training.type === "External")

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find((dept) => dept.asJson.id === departmentId)
    return department ? department.asJson.name : "Unknown Department"
  }
  const users = store.user.all.map((users) => users.asJson)
  const getName = (uid: string): string => {
    console.log("uid passed", uid)

    const currentUser = users.find((user) => user.uid === uid)
    console.log("current user", currentUser)

    return currentUser?.displayName || "Unknown Department"
  }
  const getEmail = (uid: string): string => {
    console.log("uid passed", uid)

    const currentUser = users.find((user) => user.uid === uid)
    console.log("current user", currentUser)

    return currentUser?.email || "Unknown Department"
  }
  // Handle file upload changes and update the attachment array
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const uploadedFileLinks = await Promise.all(
        Array.from(files).map(async (file) => {
          // Upload file to storage and get the link
          const uploadedFileLink = await api.uploadManager.uploadQualificationToStorage(
            file,
            `trainingQualification/${me?.uid}`,
          )
          return uploadedFileLink // Return the uploaded file link
        }),
      )

      // Add new attachments to the existing array
      setCourseRequest({
        ...courseRequest,
        attachment: [...courseRequest.attachment, ...uploadedFileLinks],
      })
    }
  }
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (courseRequest.trainingType === "External" && externalBudget) {
        const updateBudget: ITrainingBudget = {
          ...externalBudget,
          available: externalBudget?.totalAmount - courseRequest.budget,
        }
        await api.trainingBudget.update(updateBudget)
      } else if (courseRequest.trainingType === "Internal" && internalBudget) {
        const updateBudget: ITrainingBudget = {
          ...internalBudget,
          available: internalBudget?.totalAmount - courseRequest.budget,
        }
        await api.trainingBudget.update(updateBudget)
      } else if (
        courseRequest.trainingType !== "Internal" &&
        externalBudget &&
        courseRequest.trainingType !== "External"
      ) {
        const updateBudget: ITrainingBudget = {
          ...externalBudget,
          available: externalBudget?.totalAmount - courseRequest.budget,
        }
        await api.trainingBudget.update(updateBudget)
      }

      if (store.trainingRequest.selected) {
        const update: ITrainingRequest = {
          ...courseRequest,
          status: "Approved",
        }
        await api.trainingRequest.update(update)
        const emailTemplate = USER_APPROVAL_EMAIL_TEMPLATE(
          courseRequest.courseName, // Pass course name
          getDepartmentName(courseRequest.department), // Pass department name
          dateFormat_YY_MM_DD(courseRequest.startDate), // Pass formatted start date
          dateFormat_YY_MM_DD(courseRequest.endDate), // Pass formatted end date
          new Date().toISOString(), // Current date as approval date
          getName(courseRequest.uid), // Username for email signature
        )

        await api.mail.sendMail([getEmail(courseRequest.uid)], MAIL_EMAIL, [],emailTemplate.SUBJECT, emailTemplate.BODY)
      } else {
        await api.trainingRequest.create(courseRequest)
      }
    } catch (error) {
      console.error("Error submitting training request:", error)
      // Handle error feedback here
    } finally {
      setLoading(false)
      onCancel()
    }
  }

  useEffect(() => {
    // Load the selected personal development item into the state when the modal opens
    if (store.trainingRequest.selected) {
      setCourseRequest(store.trainingRequest.selected)
    }
  }, [store.trainingRequest.selected])
  const onCancel = () => {
    store.personalDevelopment.clearSelected()
    hideModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_APPROVE_TRAINING_MODAL)
  }

  const purposeOptions = ["Development", "Certification", "Other"]

  return (
    <div className="view-training-modal uk-modal-dialog uk-modal-body uk-margin-auto-vertical">
      <button className="uk-modal-close-default" type="button" data-uk-close></button>
      <div className="uk-container uk-container-large uk-margin-top">
        <form onSubmit={handleSubmit}>
          <div className="uk-width-1-1">
            <h1 className="training-title">Approve Training Request</h1>
          </div>
          <section className="uk-section uk-section-muted custom-section">
            <h2 className="section-title">Employee Information</h2>
            <div className="uk-grid uk-grid-small uk-child-width-1-2@s">
              {/* Row for Name and Email */}
              <div className="uk-margin uk-width-1-1">
                <div className="uk-grid uk-grid-small">
                  <div className="uk-width-1-2">
                    <label className="custom-label">Name:</label>
                    <input
                      className="custom-input"
                      type="text"
                      value={getName(courseRequest.uid)}
                      onChange={(e) =>
                        setCourseRequest({
                          ...courseRequest,
                          name: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="uk-width-1-2">
                    <label className="custom-label">Email:</label>
                    <input
                      className="custom-input"
                      type="text"
                      value={courseRequest.email || currentUser?.email || ""}
                      onChange={(e) =>
                        setCourseRequest({
                          ...courseRequest,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Other fields */}
              <div className="uk-margin">
                <label className="custom-label">Department:</label>
                <input className="custom-input" type="text" value={getDepartmentName(courseRequest.department)} />
              </div>
              <div className="uk-margin">
                <label className="custom-label">Phone Number:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={courseRequest.phoneNumber}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      phoneNumber: e.target.value,
                    })
                  }
                />
              </div>

              <div className="uk-margin">
                <label className="custom-label">Location:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={courseRequest.location}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      location: e.target.value,
                    })
                  }
                />
              </div>
              <div className="uk-margin">
                <label className="custom-label">Supervisor Name:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={getName(me?.supervisor ?? "")}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      supervisor: e.target.value,
                    })
                  }
                />
              </div>

              <div className="uk-margin">
                <label className="custom-label">Supervisor Phone:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={courseRequest.supervisorPhone}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      supervisorPhone: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </section>

          {/* Course Information Section */}
          {/* Course Information Section */}
          <section className="uk-section uk-section-muted custom-section">
            <h2 className="section-title">Course Information</h2>

            <div className="uk-grid uk-grid-small uk-child-width-1-2@m uk-grid-divider">
              {/* Course Name */}
              <div className="uk-margin">
                <label className="custom-label">Course Name:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={courseRequest.courseName}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      courseName: e.target.value,
                    })
                  }
                />
              </div>

              {/* Preferred Date */}
              <div className="uk-margin">
                <label className="custom-label">Preferred Date:</label>
                <input
                  className="custom-input"
                  type="date"
                  value={
                    courseRequest.preferredDate
                      ? dateFormat_YY_MM_DD(courseRequest.preferredDate) // Use the formatted date if it exists
                      : new Date().toISOString().split("T")[0] // Set to today's date if empty
                  }
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      preferredDate: e.target.valueAsNumber,
                    })
                  }
                />
              </div>
              <div className="uk-margin">
                <label className="custom-label">Start Date:</label>
                <input
                  className="custom-input"
                  type="date"
                  value={
                    courseRequest.startDate
                      ? dateFormat_YY_MM_DD(courseRequest.startDate) // Use the formatted date if it exists
                      : new Date().toISOString().split("T")[0] // Set to today's date if empty
                  }
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      startDate: e.target.valueAsNumber,
                    })
                  }
                />
              </div>
              <div className="uk-margin">
                <label className="custom-label">End Date:</label>
                <input
                  className="custom-input"
                  type="date"
                  value={
                    courseRequest.endDate
                      ? dateFormat_YY_MM_DD(courseRequest.endDate) // Use the formatted date if it exists
                      : new Date().toISOString().split("T")[0] // Set to today's date if empty
                  }
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      endDate: e.target.valueAsNumber,
                    })
                  }
                />
              </div>

              {/* Offered By */}
              <div className="uk-margin">
                <label className="custom-label">Offered By:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={courseRequest.offeredBy}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      offeredBy: e.target.value,
                    })
                  }
                />
              </div>

              {/* Duration */}
              <div className="uk-margin">
                <label className="custom-label">Duration:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={courseRequest.duration}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      duration: e.target.value,
                    })
                  }
                />
              </div>

              {/* Location */}
              <div className="uk-margin">
                <label className="custom-label">Location:</label>
                <input
                  className="custom-input"
                  type="text"
                  value={courseRequest.courseLocation}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      courseLocation: e.target.value,
                    })
                  }
                />
              </div>
              <div className="uk-margin">
                <label className="custom-label">Budget:</label>
                <input
                  className="custom-input"
                  type="number"
                  value={courseRequest.budget}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      budget: e.target.valueAsNumber,
                    })
                  }
                />
              </div>
              {/* Brief Summary */}
              <div className="uk-margin uk-width-1-1">
                <label className="custom-label">Brief Summary of Course Content:</label>
                <textarea
                  className="uk-textarea uk-margin-small-top"
                  value={courseRequest.courseSummary}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      courseSummary: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-title">Purpose of Training</h2>
            <div className="uk-margin">
              {purposeOptions.map((purpose) => (
                <label className="uk-margin-right" key={purpose}>
                  <input
                    className="uk-radio uk-margin-small-right"
                    type="radio"
                    value={purpose}
                    checked={courseRequest.purpose === purpose}
                    onChange={(e) =>
                      setCourseRequest({
                        ...courseRequest,
                        purpose: e.target.value,
                      })
                    }
                  />
                  {purpose}
                </label>
              ))}
            </div>
          </section>
          <label className="uk-margin-top">Upload Document(s)</label>
          <input type="file" multiple onChange={handleFileChange} className="uk-input uk-margin-small-top" />

          {/* Display Uploaded Attachments */}
          <div>
            <h3>Uploaded Attachments</h3>
            {courseRequest.attachment.length > 0 ? (
              <ul>
                {courseRequest.attachment.map((url, index) => (
                  <li key={index}>
                    {/* View Attachment */}
                    <button
                      type="button"
                      className="btn-view"
                      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                    >
                      View Attachment {index + 1}
                    </button>
                    {/* Remove Attachment */}
                    {/* <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveAttachment(index)}
                    >
                      Remove
                    </button> */}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No attachments found.</p>
            )}
          </div>
          {/* Confirmation Box */}
          <section className="uk-section uk-section-default">
            <div className="uk-margin">
              <label>
                <input
                  className="uk-checkbox uk-margin-small-right"
                  type="checkbox"
                  checked={courseRequest.confirmation}
                  onChange={(e) =>
                    setCourseRequest({
                      ...courseRequest,
                      confirmation: e.target.checked,
                    })
                  }
                />
                I confirm that this information is true and accurate.
              </label>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="uk-section  button-container">
            <button type="submit" className="custom-button recommend-button" disabled={loading}>
              {loading ? "Approving..." : "Approve"}
            </button>
            <button type="button" className="custom-button not-recommend-button" onClick={onCancel}>
              Not Recommend
            </button>
          </div>
        </form>
      </div>
    </div>
  )
})

export default ApproveTrainingRequest
